import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Grid,
    TextField, MenuItem, Switch, Button, Alert, CircularProgress,
} from "@mui/material";
import EventNoteIcon from "@mui/icons-material/EventNote";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";

const ORIGINS = ["custom", "system", "external"];

const EditEventTypeDialog = ({ open, eventType, onClose, onSaved }) => {
    const [form, setForm] = useState({ key: "", origin: "custom", name: "", description: "", payload_schema: "", active: true, brand: "" });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (eventType) {
            setForm({
                key: eventType.key || "",
                origin: eventType.origin || "custom",
                name: eventType.name || "",
                description: eventType.description || "",
                payload_schema: eventType.payload_schema ? JSON.stringify(eventType.payload_schema, null, 2) : "",
                active: eventType.active ?? true,
                brand: eventType.brand || "",
            });
            setError(null);
        }
    }, [eventType]);

    const handle = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
    const isValid = form.key.trim() && form.origin.trim() && form.name.trim();

    const handleSubmit = async () => {
        if (!isValid || !eventType) return;
        setSubmitting(true);
        setError(null);
        try {
            let payloadSchema = null;
            if (form.payload_schema.trim()) {
                try { payloadSchema = JSON.parse(form.payload_schema); }
                catch { throw new Error("payload_schema n'est pas un JSON valide"); }
            }
            const body = {
                key: form.key.trim(),
                origin: form.origin.trim(),
                name: form.name.trim(),
                description: form.description.trim() || null,
                payload_schema: payloadSchema,
                active: form.active,
                brand: form.brand || null,
            };
            const res = await fetch(`${LOYALTY_API_BASE}/admin/event-types/${eventType.id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "X-Brand": form.brand || eventType.brand
                },
                body: JSON.stringify(body),
            });
            if (!res.ok) { const e = await res.json().catch(() => null); throw new Error(e?.detail || `Erreur HTTP ${res.status}`); }
            onSaved(); onClose();
        } catch (err) { setError(err.message); } finally { setSubmitting(false); }
    };

    if (!eventType) return null;
    const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };
    const monoFieldSx = { ...fieldSx, "& textarea": { fontFamily: "monospace", fontSize: 12 } };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.eventTypes.gradient }}>
                        <EventNoteIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                            Modifier le Type d'Événement
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">{eventType.key}</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={7}>
                        <TextField fullWidth required label="Clé (key)" value={form.key}
                            onChange={handle("key")} size="small"
                            helperText="Identifiant technique" sx={fieldSx} />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <TextField fullWidth required select label="Origine" value={form.origin}
                            onChange={handle("origin")} size="small" sx={fieldSx}>
                            {ORIGINS.map((o) => (
                                <MenuItem key={o} value={o}>
                                    {o === "custom" ? "Custom" : o === "system" ? "System" : "External"}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth required label="Nom affiché" value={form.name}
                            onChange={handle("name")} size="small" sx={fieldSx} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Description" value={form.description}
                            onChange={handle("description")} size="small" multiline rows={2} sx={fieldSx} />
                    </Grid>

                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Box>
                                <Typography fontSize={13} fontWeight={600}>Activer</Typography>
                                <Typography fontSize={11} color="text.secondary">Type disponible pour les règles</Typography>
                            </Box>
                            <Switch checked={form.active} onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.eventTypes.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.eventTypes.main }
                                }} />
                        </Box>
                    </Grid>
                </Grid>
                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    sx={{ background: SECTION_COLORS.eventTypes.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    {submitting ? "Enregistrement..." : "Enregistrer"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditEventTypeDialog;
